import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/sonner";
import { CalendarClock, AlertTriangle, Loader2, CheckCircle2, XCircle } from "lucide-react";

const TIER_LABELS = {
  free: "Free",
  vault: "Vault",
  copilot: "Shield",
  dispute: "Dispute Mode",
  pro: "Pro",
};

const CYCLE_LABELS = {
  monthly: "monthly",
  annual: "annual",
  oneoff: "one-off",
};

// "23 Mar 2026" — short, friendly, unambiguous in AU. Avoids 03/04 confusion.
function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

/**
 * SubscriptionCard.
 *
 * Renders on the Account page for paid users. Shows:
 *  - Current plan + billing cycle + renewal date (the "you keep access until X")
 *  - Cancel button → confirmation modal that surfaces the exact end-of-access date
 *  - When already scheduled to cancel: shows the cancellation banner + "Resume"
 *
 * Free users see nothing here (Account already shows a "Change plan" CTA above).
 * Dispute Mode (one-off) shows the expiry date but no cancel button — there is
 * nothing to cancel because it's a single 30-day pass.
 */
export default function SubscriptionCard({ user }) {
  const { refreshUser } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const autoResumedRef = useRef(false);

  const isFree = !user || user.tier === "free";
  const planLabel = !isFree ? (TIER_LABELS[user.tier] || user.tier) : "";
  const cycleLabel = !isFree ? (CYCLE_LABELS[user.subscription_cycle] || user.subscription_cycle) : "";
  const renewsAt = !isFree ? fmtDate(user.subscription_renews_at) : null;
  const isOneOff = !isFree && user.subscription_cycle === "oneoff";
  const isCanceling = !isFree && !!user.subscription_cancel_at_period_end;

  const handleCancel = async () => {
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      await refreshUser();
      toast.success(`Cancelled. You keep ${planLabel} until ${renewsAt}.`);
      setConfirmOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async () => {
    setBusy(true);
    try {
      await api.post("/billing/resume");
      await refreshUser();
      toast.success("Welcome back! Your subscription is active again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resume. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Honour the resume deep-link from the cancellation email
  // (e.g. /account?resume=1). Fires exactly once per mount when the user
  // is currently in the "canceling" state, we don't want the link to
  // silently no-op for an already-active user.
  useEffect(() => {
    if (autoResumedRef.current) return;
    if (!isCanceling) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("resume") !== "1") return;
    autoResumedRef.current = true;
    // Strip the query param so a refresh doesn't re-fire.
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
    handleResume();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanceling]);

  if (isFree) return null;

  return (
    <>
      <div className="card mb-6" data-testid="subscription-card">
        <h3 className="font-serif text-xl mb-4 text-[#0D2B52]">Subscription</h3>

        {/* Renewal / end date row */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30 flex-shrink-0">
            <CalendarClock size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
          </div>
          <div className="flex-1 min-w-0">
            {isOneOff ? (
              <>
                <div className="font-serif text-[15px] text-[#1A1A1A]">
                  Dispute Mode pass active
                </div>
                <div className="text-[13px] text-[#6B7280] mt-0.5" data-testid="subscription-ends-at">
                  Ends {renewsAt || "soon"}. No subscription, nothing to cancel.
                </div>
              </>
            ) : isCanceling ? (
              <>
                <div className="font-serif text-[15px] text-[#1A1A1A]">
                  Cancellation scheduled
                </div>
                <div
                  className="text-[13px] text-[#6B7280] mt-0.5"
                  data-testid="subscription-cancels-on"
                >
                  You keep <strong>{planLabel}</strong> until{" "}
                  <strong>{renewsAt || "the end of your billing period"}</strong>. After that you&rsquo;ll move to the Free plan (read-only, no downloads).
                </div>
              </>
            ) : (
              <>
                <div className="font-serif text-[15px] text-[#1A1A1A]">
                  {planLabel} &middot; {cycleLabel}
                </div>
                <div
                  className="text-[13px] text-[#6B7280] mt-0.5"
                  data-testid="subscription-renews-on"
                >
                  Renews on <strong>{renewsAt || "your next billing date"}</strong>. Cancel anytime, you keep access until the renewal date.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E5E4DE] mt-2">
          {isCanceling ? (
            <button
              onClick={handleResume}
              disabled={busy}
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="subscription-resume-btn"
            >
              {busy ? <Loader2 size={14} strokeWidth={1.75} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={1.75} />}
              Resume subscription
            </button>
          ) : (
            !isOneOff && (
              <button
                onClick={() => setConfirmOpen(true)}
                className="text-sm border border-[#E5E4DE] text-[#6B7280] hover:text-[#B35D4C] hover:border-[#B35D4C] px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
                data-testid="subscription-cancel-btn"
              >
                <XCircle size={14} strokeWidth={1.75} />
                Cancel subscription
              </button>
            )
          )}
          <Link
            to="/pricing"
            className="text-sm border border-[#E5E4DE] text-[#0D2B52] hover:bg-[#FAF9F6] px-3 py-2 inline-flex items-center"
            data-testid="subscription-change-plan-btn"
          >
            Change plan
          </Link>
        </div>

        <p className="text-[11px] text-[#9CA3AF] mt-3 leading-snug">
          See our{" "}
          <Link to="/refunds" className="underline hover:text-[#0D2B52]" data-testid="subscription-refunds-link">
            refunds &amp; cancellations policy
          </Link>{" "}
          for the full details. Your statutory rights under the Australian Consumer Law are not affected.
        </p>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-modal-title"
          data-testid="cancel-confirm-modal"
          onClick={() => !busy && setConfirmOpen(false)}
        >
          <div
            className="bg-[#FAF9F6] border border-[#E5E4DE] max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-[#B35D4C]/10 border border-[#B35D4C]/30 flex-shrink-0">
                <AlertTriangle size={16} strokeWidth={1.75} className="text-[#B35D4C]" />
              </div>
              <div>
                <h3
                  id="cancel-modal-title"
                  className="font-serif text-xl text-[#0D2B52] leading-tight"
                >
                  Cancel your {planLabel} plan?
                </h3>
                <p className="text-[12px] text-[#6B7280] font-mono mt-1">No payment processed today.</p>
              </div>
            </div>

            <div className="border border-[#E5E4DE] bg-white px-4 py-3 mb-4">
              <div className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed">
                <div className="mb-2">
                  <strong className="text-[#1B6C62]">You keep full access until {renewsAt || "the end of your billing period"}.</strong>
                </div>
                <div className="text-[#6B7280] text-[12.5px]">
                  After that, you&rsquo;ll move to the Free plan. Your documents stay safe (read-only), but downloads, evidence bundles and Compass chats will pause until you upgrade again.
                </div>
              </div>
            </div>

            <p className="text-[11px] text-[#6B7280] mb-5 leading-snug">
              Cancel anytime. You&rsquo;ll be billed your chosen plan price until you cancel. No refunds for change of mind, your rights under the Australian Consumer Law still apply. See our{" "}
              <Link to="/refunds" className="underline hover:text-[#0D2B52]" target="_blank" rel="noopener noreferrer">
                refunds policy
              </Link>
              .
            </p>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                className="text-sm border border-[#E5E4DE] text-[#0D2B52] hover:bg-white px-4 py-2.5 transition-colors"
                data-testid="cancel-modal-keep-btn"
              >
                Keep my plan
              </button>
              <button
                onClick={handleCancel}
                disabled={busy}
                className="text-sm bg-[#B35D4C] text-white hover:bg-[#9d4f40] px-4 py-2.5 inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                data-testid="cancel-modal-confirm-btn"
              >
                {busy ? <Loader2 size={14} strokeWidth={1.75} className="animate-spin" /> : null}
                Yes, cancel on {renewsAt || "renewal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
